import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import type {
  Candidate,
  CandidateProfileIssue,
  Experience,
  Taxonomy,
} from '@axiom/validation';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  CardSection,
  Layout,
  LayoutItem,
  SmallHeader,
} from '@axiom/ui';

import { CandidateExperiencesUtil } from '../../utils/candidate-experiences-util';

import { ExperiencesItemDisplay } from './ExperiencesItemDisplay';

export const ExperiencesCard = ({
  experiences,
  taxonomy,
  candidateId,
  issues,
  name = 'EXPERIENCES',
}: {
  experiences: Experience[];
  taxonomy: Taxonomy;
  candidateId: Candidate['id'];
  issues: CandidateProfileIssue[];
  name?: string;
}) => {
  const navigate = useNavigate();
  const shouldShowMergeTip: Record<Experience['id'], boolean> = useMemo(
    () => CandidateExperiencesUtil.createShouldShowMergeTipMapping(experiences),
    [experiences]
  );
  return (
    <Card name={name}>
      <CardHeader>
        <Layout position="middle">
          <LayoutItem fluid>
            <SmallHeader name="HEADER">Experience</SmallHeader>
          </LayoutItem>
          <Button
            name="ADD_EXPERIENCE"
            pattern="secondary"
            onClick={() => {
              void navigate('../experiences/new');
            }}
            icon="plus"
            variation="minimal"
          >
            Add
          </Button>
        </Layout>
      </CardHeader>
      {experiences.map((experience, index) => (
        <CardSection
          divider
          key={experience.id}
          name={`EXPERIENCE_ITEM_${index}`}
        >
          <ExperiencesItemDisplay
            candidateId={candidateId}
            experience={experience}
            taxonomy={taxonomy}
            showMergeTip={shouldShowMergeTip[experience.id] ?? false}
            issues={issues}
            onExperienceNav={() => {
              void navigate(`../experiences/${experience.id}`);
            }}
            name={`EXPERIENCES_ITEM_DISPLAY_${index}`}
          />
        </CardSection>
      ))}
      {experiences.length === 0 && (
        <CardSection name="NO_EXPERIENCES">
          <Banner impact="low" type="info">
            No experience added
          </Banner>
        </CardSection>
      )}
    </Card>
  );
};
